import { Dispatch, useEffect } from 'react';

import { useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import { useGroupLabels } from '@/utils/hooks/swr/useGroupLabels';
import useLabels from '@/utils/hooks/swr/useLabels';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

type LabelOption = {
    label: string;
    value: string;
    type?: string;
};

type UseAlbumManualFiltersReturn = {
    labelsOptions: LabelOption[];
    selectedHashtags: string[];
    selectedLabels: LabelOption[];
    network: string | undefined;
    isStoryAlbum: boolean;
    isGroup: boolean;
    setRequestParams: Dispatch<any>;
};

const useAlbumManualFilters = (): UseAlbumManualFiltersReturn => {
    const { albumInfo } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();
    const network = activeSocialProfile?.social_network_type;
    const isStoryAlbum = albumInfo.criteria?.album_type === 'stories';

    // Fetch labels based on group or single SP
    const {
        labels: singleSPLabels,
        requestParams: singleSPRequestParams,
        setRequestParams: setSingleSPRequestParams
    } = useLabels({
        activeSPID: activeSocialProfile?.id,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        },
        shouldFetch: !isGroup
    });

    const {
        labels: groupLabels,
        requestParams: groupRequestParams,
        setRequestParams: setGroupRequestParams
    } = useGroupLabels({
        activeGroupID: String(activeGroup?.id),
        shouldFetch: isGroup && !!activeGroup?.id,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    // Use the appropriate data based on context
    const labelsOptions: LabelOption[] = isGroup
        ? groupLabels?.map((label) => ({ label: label.label, value: label.label }))
        : singleSPLabels;
    const requestParams = isGroup ? groupRequestParams : singleSPRequestParams;
    const activeSetRequestParams = isGroup ? setGroupRequestParams : setSingleSPRequestParams;

    // Set media_type for stories albums
    useEffect(() => {
        if (isStoryAlbum && activeSetRequestParams) {
            activeSetRequestParams({ media_type: 'stories' });
        }
        if (!isStoryAlbum && requestParams?.media_type && activeSetRequestParams) {
            activeSetRequestParams(undefined);
        }
    }, [isStoryAlbum, requestParams?.media_type, activeSetRequestParams]);

    // Extract selected hashtags and labels from album context
    const selectedHashtags: string[] = albumInfo?.criteria?.hashtags || [];
    const selectedLabels: LabelOption[] =
        (labelsOptions ?? [])?.filter(
            (item) => albumInfo?.criteria?.labels?.includes(item?.label)
        ) || [];

    return {
        labelsOptions,
        selectedHashtags,
        selectedLabels,
        network,
        isStoryAlbum,
        isGroup,
        setRequestParams: activeSetRequestParams
    };
};

export { useAlbumManualFilters };
